import type { CSSProperties } from 'react';
import type { ListStyle, TextElement } from '@/components/canvas/document/publicTypes';
import { isTextBlockEmpty, isTextDocumentEmpty } from '../model/textPlain';
import {
  resolveElementPlaceholder,
  resolveBlockTextStyle,
  resolveElementTextList,
  resolveTextLineContentDisplayStyle,
  resolveTextListBlockRowStyle,
  resolveTextListMarkerStyle,
  getTextLayoutFromElement,
  isVerticalTextWritingMode,
} from '../model/textStyles';
import type { TextBlockType, TextRun } from '../model/textDocument';

function unorderedMarker(type: ListStyle['unorderedMarker']): string {
  if (type === 'circle') return '○';
  if (type === 'square') return '■';
  return '•';
}

function runStyle(element: TextElement, blockType: TextBlockType, run: TextRun): CSSProperties {
  return resolveBlockTextStyle(element, blockType, run.style);
}

export function TextDocumentRenderer({
  element,
  editable = false,
}: {
  element: TextElement;
  editable?: boolean;
}) {
  const text = element.data;
  const { paragraph, writingMode } = getTextLayoutFromElement(element);
  const list = resolveElementTextList(paragraph);
  const isVertical = isVerticalTextWritingMode(writingMode);
  let orderedIndex = 0;
  const documentEmpty = isTextDocumentEmpty(text.document);
  const placeholderText = resolveElementPlaceholder(text, paragraph);

  return (
    <div
      className="page-text-document"
      data-text-document
      style={{
        width: isVertical ? 'max-content' : '100%',
        height: isVertical ? '100%' : undefined,
      }}
    >
      {text.document.blocks.map((block) => {
        const isOrdered = block.type === 'ordered-list-item';
        const isUnordered = block.type === 'unordered-list-item';
        orderedIndex = isOrdered ? orderedIndex + 1 : 0;
        const marker = isOrdered
          ? `${orderedIndex}.`
          : isUnordered
            ? unorderedMarker(list.unorderedMarker)
            : null;
        const hasContent = !isTextBlockEmpty(block);
        const showPlaceholder = !hasContent && documentEmpty;
        const blockStyle = resolveBlockTextStyle(element, block.type);
        const contentStyle: CSSProperties = resolveTextLineContentDisplayStyle(
          !!paragraph.singleLine,
          writingMode,
        );
        const isList = isOrdered || isUnordered;

        return (
          <div
            key={block.id}
            className="page-text-block"
            data-text-block-id={block.id}
            data-text-block-type={block.type}
            style={{
              textAlign: paragraph.textAlign,
              ...resolveTextListBlockRowStyle(blockStyle, list, isList, writingMode),
              ...(isVertical
                ? {
                    width: 'auto',
                    height: '100%',
                    minWidth: '1em',
                  }
                : {}),
            }}
          >
            {marker && hasContent && (
              <span
                className="page-text-list-marker"
                contentEditable={false}
                style={resolveTextListMarkerStyle(list, blockStyle)}
              >
                {marker}
              </span>
            )}
            <span className="page-text-line-content" style={contentStyle}>
              {showPlaceholder ? (
                <span
                  className="page-text-placeholder"
                  data-placeholder
                  contentEditable={false}
                  style={{ opacity: 0.45 }}
                >
                  {placeholderText}
                </span>
              ) : block.runs.length ? (
                block.runs.map((run) => (
                  <span
                    key={run.id}
                    data-text-run-id={run.id}
                    style={runStyle(element, block.type, run)}
                  >
                    {run.text}
                  </span>
                ))
              ) : (
                <br />
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
